import { useEffect, useState } from 'react';
import { useTranslation } from '@client-app/i18n';
import {
    CaretDown,
    Link,
    SlidersHorizontal,
    WarningCircle,
} from '@phosphor-icons/react';
import {
    buildMcpManualCredentialRequest,
    buildMcpOAuthDraftInput,
    normalizeMcpConnectorInput,
    parseMcpJsonImport,
    serializeMcpConnectorInputs,
    type McpConnectorAuthType,
    type McpConnectorPlatform,
    type McpCredentialRequest,
    type NormalizedMcpConnectorInput,
} from '@common/api/mcpConnectorService';
import Modal from '../Modal';
import IntegrationConnectGuide, {
    getDingTalkMcpConnectGuide,
} from './IntegrationConnectGuide';

export type McpConnectorModalMode = 'url' | 'manual';
type McpDirectAuthType = 'OAUTH' | McpConnectorAuthType;
type DingTalkMcpInputMode = 'single' | 'batch';

const DINGTALK_MCP_CONNECT_GUIDE = getDingTalkMcpConnectGuide();

export interface McpBatchConnectResult {
    failedInputs: NormalizedMcpConnectorInput[];
    errorMessage?: string;
}

interface McpConnectorModalProps {
    isOpen: boolean;
    mode: McpConnectorModalMode | null;
    showManualSetup: boolean;
    onClose: () => void;
    onStartOAuth: (input: NormalizedMcpConnectorInput) => Promise<void>;
    onConnectDirect: (input: NormalizedMcpConnectorInput) => Promise<void>;
    onConnectBatch?: (
        inputs: NormalizedMcpConnectorInput[],
    ) => Promise<McpBatchConnectResult | void>;
    onSubmitManual: (credential: McpCredentialRequest | null) => Promise<void>;
    isLoading?: boolean;
    errorText?: string | null;
    embedded?: boolean;
    platform?: McpConnectorPlatform;
}

export default function McpConnectorModal({
    isOpen,
    mode,
    showManualSetup,
    onClose,
    onStartOAuth,
    onConnectDirect,
    onConnectBatch,
    onSubmitManual,
    isLoading = false,
    errorText = null,
    embedded = false,
    platform = 'OTHER',
}: McpConnectorModalProps) {
    const { t } = useTranslation();
    const [displayName, setDisplayName] = useState('');
    const [serverUrl, setServerUrl] = useState('');
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [directAuthType, setDirectAuthType] =
        useState<McpDirectAuthType>('OAUTH');
    const [authType, setAuthType] = useState<McpConnectorAuthType>('NONE');
    const [apiKeyHeaderName, setApiKeyHeaderName] = useState('x-api-key');
    const [secret, setSecret] = useState('');
    const [dingtalkInputMode, setDingTalkInputMode] =
        useState<DingTalkMcpInputMode>('single');
    const [batchJson, setBatchJson] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setDisplayName('');
        setServerUrl('');
        setIsAdvancedOpen(false);
        setDirectAuthType(platform === 'DINGTALK' ? 'NONE' : 'OAUTH');
        setAuthType('NONE');
        setApiKeyHeaderName('x-api-key');
        setSecret('');
        setDingTalkInputMode('single');
        setBatchJson('');
        setLocalError(null);
    }, [isOpen, platform]);

    if (!isOpen || !mode) {
        return null;
    }

    const isManual = mode === 'manual' && showManualSetup;
    const isDingTalk = platform === 'DINGTALK';
    const isDingTalkBatch =
        isDingTalk && !isManual && dingtalkInputMode === 'batch';
    const displayedError = localError || errorText;
    const canStartOAuth = Boolean(displayName.trim() && serverUrl.trim());
    const canSubmitDirect =
        directAuthType === 'OAUTH'
            ? canStartOAuth
            : canStartOAuth &&
              (directAuthType === 'NONE' ||
                  Boolean(
                      secret.trim() &&
                      (directAuthType !== 'API_KEY_HEADER' ||
                          apiKeyHeaderName.trim()),
                  ));
    const canSubmitManual =
        authType === 'NONE' ||
        Boolean(
            secret.trim() &&
            (authType !== 'API_KEY_HEADER' || apiKeyHeaderName.trim()),
        );
    const canSubmitBatch = Boolean(batchJson.trim() && onConnectBatch);

    const handleStartOAuth = async () => {
        if (!canStartOAuth || isLoading) {
            return;
        }

        setLocalError(null);
        try {
            await onStartOAuth(
                buildMcpOAuthDraftInput({
                    displayName,
                    serverUrl,
                    platform,
                }),
            );
        } catch (error) {
            setLocalError(
                error instanceof Error
                    ? error.message
                    : t('website.integrationsSettings.mcpSaveFailed'),
            );
        }
    };

    const buildDirectInput = () => {
        if (directAuthType === 'NONE') {
            return normalizeMcpConnectorInput({
                source: 'custom',
                displayName,
                serverUrl,
                platform,
            });
        }

        if (directAuthType === 'BEARER') {
            return normalizeMcpConnectorInput({
                source: 'custom',
                displayName,
                serverUrl,
                platform,
                headers: [
                    { name: 'Authorization', value: `Bearer ${secret.trim()}` },
                ],
            });
        }

        return normalizeMcpConnectorInput({
            source: 'custom',
            displayName,
            serverUrl,
            platform,
            headers: [{ name: apiKeyHeaderName, value: secret }],
        });
    };

    const handleDirectSubmit = async () => {
        if (!canSubmitDirect || isLoading) {
            return;
        }

        setLocalError(null);
        try {
            await onConnectDirect(buildDirectInput());
        } catch (error) {
            setLocalError(
                error instanceof Error
                    ? error.message
                    : t('website.integrationsSettings.mcpSaveFailed'),
            );
        }
    };

    const handleManualSubmit = async () => {
        if (!canSubmitManual || isLoading) {
            return;
        }

        setLocalError(null);
        try {
            await onSubmitManual(
                buildMcpManualCredentialRequest({
                    authType,
                    secret,
                    apiKeyHeaderName,
                }),
            );
        } catch (error) {
            setLocalError(
                error instanceof Error
                    ? error.message
                    : t('website.integrationsSettings.mcpSaveFailed'),
            );
        }
    };

    const handleBatchSubmit = async () => {
        if (!canSubmitBatch || isLoading || !onConnectBatch) {
            return;
        }

        setLocalError(null);
        try {
            const parsed = parseMcpJsonImport(batchJson, {
                platform: 'DINGTALK',
            });
            if (parsed.unsupported.length > 0) {
                const details = parsed.unsupported
                    .map((item) => `${item.name}: ${item.reason}`)
                    .join('; ');
                throw new Error(
                    t(
                        'website.integrationsSettings.dingtalkMcpImportUnsupported',
                        { details },
                    ),
                );
            }
            if (parsed.connectors.length === 0) {
                throw new Error(
                    t('website.integrationsSettings.dingtalkMcpImportEmpty'),
                );
            }
            const result = await onConnectBatch(parsed.connectors);
            if (result?.failedInputs.length) {
                setBatchJson(
                    serializeMcpConnectorInputs(result.failedInputs),
                );
                setLocalError(
                    result.errorMessage ||
                        t(
                            'website.integrationsSettings.dingtalkMcpImportInvalid',
                        ),
                );
            }
        } catch (error) {
            setLocalError(
                error instanceof Error
                    ? error.message
                    : t(
                          'website.integrationsSettings.dingtalkMcpImportInvalid',
                      ),
            );
        }
    };

    const content = (
        <div className="space-y-5">
            {!isManual ? (
                <>
                    {isDingTalk ? (
                        <>
                            <IntegrationConnectGuide
                                guide={DINGTALK_MCP_CONNECT_GUIDE}
                            />
                            <div className="flex gap-2 rounded-lg border border-[#F2D99B] bg-[#FFF9EA] px-3 py-2 text-xs leading-5 text-[#795B13]">
                                <WarningCircle
                                    size={17}
                                    weight="fill"
                                    className="mt-0.5 shrink-0"
                                />
                                <span>
                                    {t(
                                        'website.integrationsSettings.dingtalkMcpSensitiveWarning',
                                    )}
                                </span>
                            </div>
                        </>
                    ) : (
                        <p className="text-sm leading-6 text-[#666F8D]">
                            {t(
                                'website.integrationsSettings.mcpOAuthDescription',
                            )}
                        </p>
                    )}
                    {isDingTalk && onConnectBatch ? (
                        <div
                            role="tablist"
                            aria-label={t(
                                'website.integrationsSettings.dingtalkMcpInputMethod',
                            )}
                            className="grid grid-cols-2 gap-1 rounded-lg bg-[#F3F5F9] p-1"
                        >
                            {(['single', 'batch'] as const).map((inputMode) => (
                                <button
                                    key={inputMode}
                                    type="button"
                                    role="tab"
                                    aria-selected={
                                        dingtalkInputMode === inputMode
                                    }
                                    onClick={() => {
                                        setDingTalkInputMode(inputMode);
                                        setLocalError(null);
                                    }}
                                    disabled={isLoading}
                                    className={`rounded-md px-3 py-2 text-sm font-medium ${
                                        dingtalkInputMode === inputMode
                                            ? 'bg-white text-[#1A162F] shadow-sm'
                                            : 'text-[#68708A]'
                                    }`}
                                >
                                    {t(
                                        inputMode === 'single'
                                            ? 'website.integrationsSettings.dingtalkMcpSingleInput'
                                            : 'website.integrationsSettings.dingtalkMcpBatchInput',
                                    )}
                                </button>
                            ))}
                        </div>
                    ) : null}
                    {isDingTalkBatch ? (
                        <label className="block text-sm font-medium text-[#1A162F]">
                            {t(
                                'website.integrationsSettings.dingtalkMcpBatchJsonLabel',
                            )}
                            <textarea
                                value={batchJson}
                                onChange={(event) =>
                                    setBatchJson(event.target.value)
                                }
                                placeholder={t(
                                    'website.integrationsSettings.dingtalkMcpBatchJsonPlaceholder',
                                )}
                                disabled={isLoading}
                                rows={8}
                                spellCheck={false}
                                className="mt-1.5 w-full resize-y rounded-lg border border-[#D9DDE8] px-3 py-2.5 font-mono text-xs leading-5 outline-none focus:border-[#1A162F]"
                            />
                            <span className="mt-1.5 block text-xs font-normal leading-5 text-[#7A839E]">
                                {t(
                                    'website.integrationsSettings.dingtalkMcpBatchJsonHelp',
                                )}
                            </span>
                        </label>
                    ) : (
                        <div className="space-y-4">
                            <label className="block text-sm font-medium text-[#1A162F]">
                                {t(
                                    'website.integrationsSettings.mcpServerName',
                                )}
                                <input
                                    value={displayName}
                                    onChange={(event) =>
                                        setDisplayName(event.target.value)
                                    }
                                    placeholder={t(
                                        isDingTalk
                                            ? 'website.integrationsSettings.dingtalkMcpServerNamePlaceholder'
                                            : 'website.integrationsSettings.mcpServerNamePlaceholder',
                                    )}
                                    disabled={isLoading}
                                    className="mt-1.5 w-full rounded-lg border border-[#D9DDE8] px-3 py-2.5 text-sm outline-none focus:border-[#1A162F]"
                                    autoFocus
                                />
                            </label>
                            <label className="block text-sm font-medium text-[#1A162F]">
                                {t('website.integrationsSettings.mcpServerUrl')}
                                <span className="mt-1.5 flex items-center rounded-lg border border-[#D9DDE8] px-3 focus-within:border-[#1A162F]">
                                    <Link
                                        size={16}
                                        className="mr-2 shrink-0 text-[#8A93B2]"
                                    />
                                    <input
                                        value={serverUrl}
                                        onChange={(event) =>
                                            setServerUrl(event.target.value)
                                        }
                                        placeholder={
                                            isDingTalk
                                                ? 'https://mcp-gw.dingtalk.com/server/...'
                                                : 'https://mcp.yourserver.com/mcp'
                                        }
                                        disabled={isLoading}
                                        className="min-w-0 flex-1 py-2.5 text-sm outline-none"
                                    />
                                </span>
                                {isDingTalk ? (
                                    <span className="mt-1.5 block text-xs font-normal leading-5 text-[#7A839E]">
                                        {t(
                                            'website.integrationsSettings.dingtalkMcpUrlHelp',
                                        )}
                                    </span>
                                ) : null}
                            </label>
                            {!isDingTalk ? (
                                <section
                                    data-testid="mcp-advanced-settings"
                                    className="rounded-xl border border-[#E8EBF3] bg-white"
                                >
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setIsAdvancedOpen(
                                                (previous) => !previous,
                                            )
                                        }
                                        disabled={isLoading}
                                        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A162F]">
                                            <SlidersHorizontal
                                                size={17}
                                                weight="regular"
                                            />
                                            {t(
                                                'website.integrationsSettings.mcpAdvancedSettings',
                                            )}
                                            <span className="text-sm font-normal text-[#7A839E]">
                                                {`(${t('website.integrationsSettings.optional')})`}
                                            </span>
                                        </span>
                                        <CaretDown
                                            size={18}
                                            weight="regular"
                                            className={`text-[#7A839E] transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    {isAdvancedOpen ? (
                                        <div className="space-y-3 border-t border-[#EEF0F5] px-4 py-4">
                                            <label className="block text-sm font-medium text-[#1A162F]">
                                                {t(
                                                    'website.integrationsSettings.mcpManualAuthType',
                                                )}
                                                <select
                                                    value={directAuthType}
                                                    onChange={(event) => {
                                                        setDirectAuthType(
                                                            event.target
                                                                .value as McpDirectAuthType,
                                                        );
                                                        setSecret('');
                                                    }}
                                                    disabled={isLoading}
                                                    className="mt-1.5 h-10 w-full rounded-lg border border-[#D9DDE8] bg-white px-3 text-sm outline-none focus:border-[#1A162F]"
                                                >
                                                    <option value="OAUTH">
                                                        {t(
                                                            'website.integrationsSettings.mcpOAuthMethod',
                                                        )}
                                                    </option>
                                                    <option value="NONE">
                                                        {t(
                                                            'website.integrationsSettings.mcpManualNone',
                                                        )}
                                                    </option>
                                                    <option value="BEARER">
                                                        {t(
                                                            'website.integrationsSettings.mcpManualBearer',
                                                        )}
                                                    </option>
                                                    <option value="API_KEY_HEADER">
                                                        {t(
                                                            'website.integrationsSettings.mcpManualApiKey',
                                                        )}
                                                    </option>
                                                </select>
                                            </label>
                                            {directAuthType ===
                                            'API_KEY_HEADER' ? (
                                                <label className="block text-sm font-medium text-[#1A162F]">
                                                    {t(
                                                        'website.integrationsSettings.mcpHeaderName',
                                                    )}
                                                    <input
                                                        value={apiKeyHeaderName}
                                                        onChange={(event) =>
                                                            setApiKeyHeaderName(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                        disabled={isLoading}
                                                        className="mt-1.5 w-full rounded-lg border border-[#D9DDE8] px-3 py-2.5 text-sm outline-none focus:border-[#1A162F]"
                                                    />
                                                </label>
                                            ) : null}
                                            {directAuthType !== 'OAUTH' &&
                                            directAuthType !== 'NONE' ? (
                                                <label className="block text-sm font-medium text-[#1A162F]">
                                                    {directAuthType ===
                                                    'API_KEY_HEADER'
                                                        ? t(
                                                              'website.integrationsSettings.mcpHeaderValue',
                                                          )
                                                        : t(
                                                              'website.integrationsSettings.mcpManualSecret',
                                                          )}
                                                    <input
                                                        type="password"
                                                        value={secret}
                                                        onChange={(event) =>
                                                            setSecret(
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                        disabled={isLoading}
                                                        className="mt-1.5 w-full rounded-lg border border-[#D9DDE8] px-3 py-2.5 text-sm outline-none focus:border-[#1A162F]"
                                                    />
                                                </label>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </section>
                            ) : null}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <p className="text-sm leading-6 text-[#666F8D]">
                        {t(
                            'website.integrationsSettings.mcpManualSetupDescription',
                        )}
                    </p>
                    <label className="block text-sm font-medium text-[#1A162F]">
                        {t('website.integrationsSettings.mcpManualAuthType')}
                        <select
                            value={authType}
                            onChange={(event) =>
                                setAuthType(
                                    event.target.value as McpConnectorAuthType,
                                )
                            }
                            disabled={isLoading}
                            className="mt-1.5 h-10 w-full rounded-lg border border-[#D9DDE8] bg-white px-3 text-sm outline-none focus:border-[#1A162F]"
                        >
                            <option value="NONE">
                                {t(
                                    'website.integrationsSettings.mcpManualNone',
                                )}
                            </option>
                            <option value="BEARER">
                                {t(
                                    'website.integrationsSettings.mcpManualBearer',
                                )}
                            </option>
                            <option value="API_KEY_HEADER">
                                {t(
                                    'website.integrationsSettings.mcpManualApiKey',
                                )}
                            </option>
                        </select>
                    </label>
                    {authType === 'API_KEY_HEADER' ? (
                        <label className="block text-sm font-medium text-[#1A162F]">
                            {t('website.integrationsSettings.mcpHeaderName')}
                            <input
                                value={apiKeyHeaderName}
                                onChange={(event) =>
                                    setApiKeyHeaderName(event.target.value)
                                }
                                disabled={isLoading}
                                className="mt-1.5 w-full rounded-lg border border-[#D9DDE8] px-3 py-2.5 text-sm outline-none focus:border-[#1A162F]"
                            />
                        </label>
                    ) : null}
                    {authType !== 'NONE' ? (
                        <label className="block text-sm font-medium text-[#1A162F]">
                            {t('website.integrationsSettings.mcpManualSecret')}
                            <input
                                type="password"
                                value={secret}
                                onChange={(event) =>
                                    setSecret(event.target.value)
                                }
                                disabled={isLoading}
                                className="mt-1.5 w-full rounded-lg border border-[#D9DDE8] px-3 py-2.5 text-sm outline-none focus:border-[#1A162F]"
                            />
                        </label>
                    ) : null}
                </>
            )}
            {displayedError ? (
                <p role="alert" className="text-sm text-red-600">
                    {displayedError}
                </p>
            ) : null}
            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="rounded-lg border border-[#D9DDE8] px-4 py-2 text-sm font-medium text-[#666F8D] disabled:opacity-60"
                >
                    {t('common.cancel')}
                </button>
                <button
                    type="button"
                    onClick={() =>
                        void (isManual
                            ? handleManualSubmit()
                            : isDingTalkBatch
                              ? handleBatchSubmit()
                              : directAuthType === 'OAUTH'
                                ? handleStartOAuth()
                                : handleDirectSubmit())
                    }
                    disabled={
                        isLoading ||
                        (isManual
                            ? !canSubmitManual
                            : isDingTalkBatch
                              ? !canSubmitBatch
                              : !canSubmitDirect)
                    }
                    className="rounded-lg bg-[#1A162F] px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
                >
                    {isLoading
                        ? t('website.integrationsSettings.mcpSaving')
                        : isManual
                          ? t('website.integrationsSettings.mcpSaveAndSync')
                          : isDingTalkBatch
                            ? t(
                                  'website.integrationsSettings.dingtalkMcpImportAndConnect',
                              )
                            : directAuthType === 'OAUTH'
                              ? t(
                                    'website.integrationsSettings.mcpConnectWithOAuth',
                                )
                              : t(
                                    'website.integrationsSettings.mcpSaveAndSync',
                                )}
                </button>
            </div>
        </div>
    );

    if (embedded) {
        return content;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                if (!isLoading) {
                    onClose();
                }
            }}
            title={
                isManual
                    ? t('website.integrationsSettings.mcpManualSetupTitle')
                    : t('website.integrationsSettings.mcpUrlTitle')
            }
            width="max-w-[560px]"
            bodyClassName="px-6 pb-6 pt-2"
        >
            {content}
        </Modal>
    );
}
